import { motion } from "framer-motion";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { BidValue, Card, HandScore, SeatIndex, Suit, TeamId } from "../types/game";

export const SUIT_SYMBOL: Record<Suit, string> = { C: "♣", D: "♦", H: "♥", S: "♠" };
export const SUIT_LABEL: Record<Suit, string> = { C: "Clubs", D: "Diamonds", H: "Hearts", S: "Spades" };
export const SUIT_IS_RED: Record<Suit, boolean> = { C: false, D: true, H: true, S: false };
/** Corner + center ink — black suits read clearly on white stock. */
const SUIT_COLOR_CLASS: Record<Suit, string> = {
  C: "text-neutral-950",
  D: "text-red-600",
  H: "text-rose-600",
  S: "text-neutral-950",
};

/** Center suit pip on standard (non-handheld) `PlayingCard` — partner trump matches this scale. */
const PLAYING_CARD_TABLE_CENTER_PIP =
  "text-[3.25rem] sm:text-[3.1rem] min-[768px]:max-[1535px]:text-[4rem]";

/** Trump-choice tiles: match playing-card suit colors for quick scanning. */
const TRUMP_BTN_TABLET = "min-[768px]:max-[1535px]:px-5 min-[768px]:max-[1535px]:py-4 min-[768px]:max-[1535px]:text-lg ";

export const TRUMP_CHOICE_BUTTON_CLASS: Record<Suit, string> = {
  C:
    "border-2 border-neutral-700/60 bg-gradient-to-br from-zinc-100 via-white to-zinc-50 " +
    "text-neutral-950 hover:from-zinc-200 hover:to-zinc-100 shadow-sm " +
    TRUMP_BTN_TABLET,
  D:
    "border-2 border-red-500/55 bg-gradient-to-br from-rose-100 via-white to-red-50 " +
    "text-red-700 hover:from-rose-200 hover:to-rose-100 shadow-sm " +
    TRUMP_BTN_TABLET,
  H:
    "border-2 border-rose-500/55 bg-gradient-to-br from-pink-100 via-white to-rose-50 " +
    "text-rose-800 hover:from-rose-200 hover:to-pink-100 shadow-sm " +
    TRUMP_BTN_TABLET,
  S:
    "border-2 border-neutral-800/55 bg-gradient-to-br from-zinc-200 via-white to-slate-100 " +
    "text-neutral-950 hover:from-zinc-300 hover:to-zinc-100 shadow-sm " +
    TRUMP_BTN_TABLET,
};

export function rankLabel(rank: Card["rank"]): string {
  if (rank === 11) return "J";
  if (rank === 12) return "Q";
  if (rank === 13) return "K";
  if (rank === 14) return "A";
  return String(rank);
}

// Corners: rank + suit top-left upright; bottom-right also upright on screen (no upside-down
// mirror — easier on monitors). Center is large suit pip — identity reads from corners.
export function PlayingCard({
  card, onClick, disabled, highlight, handheld,
}: {
  card: Card;
  onClick?: () => void;
  disabled?: boolean;
  highlight?: boolean;
  /** Larger footprint on phones — use for the bottom hand fan only (table uses default). */
  handheld?: boolean;
}) {
  const red = SUIT_IS_RED[card.suit];
  const rankTxt = rankLabel(card.rank);
  const suitTxt = SUIT_SYMBOL[card.suit];
  const suitColorClass = SUIT_COLOR_CLASS[card.suit];
  const colorClass = red ? "text-red-600" : "text-neutral-950";
  // Phones / tablets: handheld fan keeps large targets; 768–1535 gets a second larger tier (iPad-class);
  // shrink only once the viewport clears `min-[1536px]`, in sync with `touchHandCardWidthPx` + HandFan math.
  const baseFrame =
    "relative rounded-lg bg-gradient-to-br from-white to-slate-100 " +
    (handheld
      ? /* Phone <768; tablet 768–1535 (wider screens get bigger touch cards); desktop ≥1536. */
        "w-[5.625rem] h-[8.4375rem] min-[768px]:max-[1535px]:w-[6.75rem] min-[768px]:max-[1535px]:h-[10.125rem] " +
        "min-[1536px]:w-16 min-[1536px]:h-24 "
      : /* Table trick / bids: bump on tablet band to match enlarged felt layout. */
        "w-[4.875rem] h-[7.3125rem] sm:w-16 sm:h-24 " +
        "min-[768px]:max-[1535px]:w-[6rem] min-[768px]:max-[1535px]:h-[9rem] ") +
    "shadow-[0_2px_6px_rgba(0,0,0,0.35)] border border-slate-300/60 " +
    "select-none overflow-hidden";

  const interactive = onClick
    ? (disabled
        ? " opacity-40 cursor-not-allowed"
        : " sm:hover:-translate-y-2 sm:hover:shadow-[0_10px_20px_rgba(0,0,0,0.5)] " +
          "transition-transform duration-150 cursor-pointer")
    : "";

  const ringClass = highlight
    ? " ring-2 ring-emerald-400 ring-offset-2 ring-offset-transparent"
    : "";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={baseFrame + " " + colorClass + interactive + ringClass}
    >
      <CornerIndex handheld={handheld} rank={rankTxt} suit={suitTxt} colorClass={suitColorClass} position="tl" />
      <CornerIndex handheld={handheld} rank={rankTxt} suit={suitTxt} colorClass={suitColorClass} position="br" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-0.5">
        <span
          className={
            (handheld
              ? "text-[3.85rem] min-[768px]:max-[1535px]:text-[4.5rem] min-[1536px]:text-[3.35rem] "
              : `${PLAYING_CARD_TABLE_CENTER_PIP} `) +
            "leading-none select-none " +
            suitColorClass
          }
          aria-hidden
        >
          {suitTxt}
        </span>
      </div>
    </button>
  );
}

function CornerIndex({
  rank, suit, colorClass, position, handheld,
}: { rank: string; suit: string; colorClass: string; position: "tl" | "br"; handheld?: boolean }) {
  const pos =
    position === "tl"
      ? "top-1 left-1 sm:top-1.5 sm:left-2"
      : "bottom-1 right-1.5 sm:bottom-1.5 sm:right-2";
  const idx = handheld
    ? "text-[21px] leading-none min-[768px]:max-[1535px]:text-[24px] min-[1536px]:text-[17px]"
    : "text-[18px] sm:text-[15px] min-[768px]:max-[1535px]:text-[17px]";
  return (
    <div
      className={
        "absolute flex flex-col items-center gap-px leading-[0.95] font-black " +
        colorClass +
        " " +
        pos
      }
    >
      <div className={idx}>{rank}</div>
      <div className={idx}>{suit}</div>
    </div>
  );
}

// Slot the "played card" in the trick area for a given seat. Animates in
// from the seat's direction, pulses when this seat wins the trick, and
// (when `winnerDir` is set) flies off toward the winner on exit so the
// whole trick visually sweeps to the seat that took it.
function DealerChipTable() {
  return (
    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full border border-amber-300/60 bg-amber-400/20 text-amber-100 whitespace-nowrap shrink-0">
      Dealer
    </span>
  );
}

export function PlayedCard({
  card, tag, isWinner, seatDir, winnerDir, showDealer, flightOut = false,
}: {
  card: Card;
  tag: string;
  isWinner?: boolean;
  // Which on-screen edge the owning seat sits at (used so plays visually
  // travel from their seat into the center of the table).
  seatDir?: "top" | "bottom" | "left" | "right";
  // Which on-screen edge the trick's winning seat sits at. When set, the
  // card flies toward that edge as it exits instead of just fading.
  winnerDir?: "top" | "bottom" | "left" | "right";
  /** Amber dealer pill beside the seat tag (e.g. You + Dealer at bottom of table). */
  showDealer?: boolean;
  /** Fly cards off toward winner — driven by animate (do not swap to “empty slot” placeholders). */
  flightOut?: boolean;
}) {
  const fromOffset = dirOffset(seatDir, 72, 96);
  const toOffset = dirOffset(winnerDir, 184, 248);

  return (
    <div className="flex flex-col items-center">
      <motion.div
        key={`${card.suit}${card.rank}`}
        initial={{ opacity: 0, scale: 0.5, ...fromOffset }}
        animate={
          flightOut
            ? winnerDir
              ? { opacity: 0, scale: 0.55, x: toOffset.x, y: toOffset.y }
              : { opacity: 0, scale: 0.85, x: 0, y: 0 }
            : isWinner
              ? { opacity: 1, scale: 1.03, x: 0, y: 0 }
              : { opacity: 1, scale: 1, x: 0, y: 0 }
        }
        transition={
          flightOut
            ? winnerDir
              ? { duration: 0.45, ease: "easeIn" }
              : { duration: 0.2, ease: "easeOut" }
            : { type: "spring", stiffness: 280, damping: 24 }
        }
        className={isWinner && !flightOut ? "rounded-lg shadow-[0_0_18px_rgba(52,211,153,0.6)]" : ""}
      >
        <PlayingCard card={card} highlight={!!isWinner && !flightOut} />
      </motion.div>
      <div className="mt-1 flex items-center justify-center gap-1 flex-wrap max-w-[11rem] min-[768px]:max-[1535px]:max-w-[14rem]">
        <span
          className={
            "text-[10px] " +
            (isWinner ? "text-emerald-300 font-semibold" : "text-white/50")
          }
        >
          {tag}
          {isWinner ? " · won" : ""}
        </span>
        {showDealer ? <DealerChipTable /> : null}
      </div>
    </div>
  );
}

function dirOffset(
  dir: "top" | "bottom" | "left" | "right" | undefined,
  vMag: number,
  hMag: number,
): { x: number; y: number } {
  switch (dir) {
    case "top": return { x: 0, y: -vMag };
    case "bottom": return { x: 0, y: vMag };
    case "left": return { x: -hMag, y: 0 };
    case "right": return { x: hMag, y: 0 };
    default: return { x: 0, y: 0 };
  }
}

function seatForDirection(
  seatDir: (s: SeatIndex) => "top" | "bottom" | "left" | "right",
  dir: "top" | "bottom" | "left" | "right",
): SeatIndex {
  for (let s = 0; s < 4; s++) {
    const si = s as SeatIndex;
    if (seatDir(si) === dir) return si;
  }
  return 0;
}

/**
 * Current trick: each card sits in the compass slot for the seat that played it
 * (same geometry as bidding). Lead/follow order follows suit play rules; the
 * physical position shows who played what.
 */
export function TrickPlaysAtSeats({
  centerLabel,
  plays,
  hideFrozenCards,
  winnerSeat,
  winnerDir,
  seatDir,
  nameFor,
  showDealer,
}: {
  centerLabel: ReactNode;
  plays: { seat: SeatIndex; card: Card }[];
  hideFrozenCards: boolean;
  winnerSeat?: SeatIndex;
  winnerDir?: "top" | "bottom" | "left" | "right";
  seatDir: (seat: SeatIndex) => "top" | "bottom" | "left" | "right";
  nameFor: (seat: SeatIndex) => string;
  showDealer: (seat: SeatIndex) => boolean;
}) {
  const topSeat = seatForDirection(seatDir, "top");
  const leftSeat = seatForDirection(seatDir, "left");
  const rightSeat = seatForDirection(seatDir, "right");
  const bottomSeat = seatForDirection(seatDir, "bottom");

  const renderSeat = (seat: SeatIndex) => {
    const p = plays.find(x => x.seat === seat);
    if (!p) {
      return (
        <div
          className={
            "flex flex-col items-center justify-end w-16 min-h-[7.5rem] shrink-0 " +
            "min-[768px]:max-[1535px]:w-[6rem] min-[768px]:max-[1535px]:min-h-[9rem]"
          }
          aria-hidden
        />
      );
    }
    return (
      <PlayedCard
        key={`${p.seat}-${p.card.suit}${p.card.rank}`}
        card={p.card}
        flightOut={hideFrozenCards}
        tag={nameFor(seat)}
        isWinner={winnerSeat === seat}
        seatDir={seatDir(seat)}
        winnerDir={winnerDir}
        showDealer={showDealer(seat)}
      />
    );
  };

  return (
    <div className="flex flex-col flex-1 w-full min-h-0 px-0.5">
      <FourSeatTrickLayout
        top={renderSeat(topSeat)}
        left={renderSeat(leftSeat)}
        center={<div className="flex flex-col items-center justify-center gap-1">{centerLabel}</div>}
        right={renderSeat(rightSeat)}
        bottom={renderSeat(bottomSeat)}
      />
    </div>
  );
}

/**
 * Bidding: four compass seats for bid pills / placeholders.
 */
export function FourSeatTrickLayout({
  top, left, center, right, bottom,
}: {
  top: ReactNode;
  left: ReactNode;
  center: ReactNode;
  right: ReactNode;
  bottom: ReactNode;
}) {
  return (
    <div
      className={
        "relative w-full flex-1 overflow-visible pt-1 px-0.5 pb-12 " +
        "min-h-[min(320px,40svh)] sm:min-h-[min(340px,42svh)] " +
        "min-[768px]:max-[1535px]:min-h-[min(500px,58svh)] min-[768px]:max-[1535px]:pb-14"
      }
    >
      <div className="absolute left-1/2 top-[4%] sm:top-[6%] min-[768px]:max-[1535px]:top-[3%] -translate-x-1/2 z-10 flex justify-center max-w-[44vw]">
        {top}
      </div>
      <div className="absolute left-[1%] sm:left-[3%] top-1/2 -translate-y-1/2 z-10 flex justify-start max-w-[40%] min-[768px]:max-[1535px]:max-w-[42%]">
        {left}
      </div>
      <div className="absolute right-[1%] sm:right-[3%] top-1/2 -translate-y-1/2 z-10 flex justify-end max-w-[40%] min-[768px]:max-[1535px]:max-w-[42%]">
        {right}
      </div>
      <div className="absolute left-1/2 bottom-[2%] sm:bottom-[4%] min-[768px]:max-[1535px]:bottom-[3%] -translate-x-1/2 z-10 flex justify-center max-w-[44vw]">
        {bottom}
      </div>
      <div className="absolute inset-0 z-[1] flex items-center justify-center pointer-events-none px-[16%] py-[20%] sm:px-[18%] sm:py-[22%] min-[768px]:max-[1535px]:px-[15%] min-[768px]:max-[1535px]:py-[18%]">
        <div className="pointer-events-auto text-center max-w-[min(100%,14rem)] min-[768px]:max-[1535px]:max-w-[min(100%,20rem)]">
          {center}
        </div>
      </div>
    </div>
  );
}

/** Bid / waiting slots use the same footprint as `PlayingCard` in the table trick band. */
const BID_CARD_FRAME =
  "w-16 h-24 rounded-lg min-[768px]:max-[1535px]:w-[6.25rem] min-[768px]:max-[1535px]:h-[9.375rem]";

export function BidPill({
  bid, tag, coked, showDealer,
}: { bid?: BidValue | "pass"; tag: string; coked?: boolean; showDealer?: boolean }) {
  const tagRow = (cls: string, content: ReactNode) => (
    <div
      className={
        "mt-1 flex items-center justify-center gap-1 flex-wrap max-w-[11rem] min-[768px]:max-[1535px]:max-w-[14rem] " +
        cls
      }
    >
      {content}
      {showDealer ? <DealerChipTable /> : null}
    </div>
  );
  if (coked) {
    return (
      <div className="flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 280, damping: 20 }}
          className={
            BID_CARD_FRAME + " bg-amber-400/20 border border-amber-300/50 flex flex-col items-center justify-center"
          }
        >
          <div className="text-amber-200 text-[10px] uppercase tracking-wide">Bid</div>
          <div className="text-amber-200 font-bold text-xs">COKE</div>
        </motion.div>
        {tagRow("font-semibold", <span className="text-[10px] text-amber-200">{tag}</span>)}
      </div>
    );
  }
  if (bid === undefined) {
    return (
      <div className="flex flex-col items-center">
        <div
          className={
            "relative " +
            BID_CARD_FRAME +
            " border border-dashed border-white/15 flex items-center justify-center overflow-hidden"
          }
        >
          <div className="absolute inset-0 animate-pulse bg-white/[0.03]" />
          <div className="relative text-white/30 text-[10px] uppercase tracking-wide">
            waiting
          </div>
        </div>
        {tagRow("", <span className="text-[10px] text-white/40">{tag}</span>)}
      </div>
    );
  }
  if (bid === "pass") {
    return (
      <div className="flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 280, damping: 20 }}
          className={
            BID_CARD_FRAME + " bg-white/10 border border-white/15 flex flex-col items-center justify-center"
          }
        >
          <div className="text-white/50 text-[10px] uppercase tracking-wide">Bid</div>
          <div className="text-white/60 font-bold text-sm min-[768px]:max-[1535px]:text-base">PASS</div>
        </motion.div>
        {tagRow("", <span className="text-[10px] text-white/50">{tag}</span>)}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 280, damping: 20 }}
          className={
            BID_CARD_FRAME + " bg-emerald-500/20 border border-emerald-400/40 flex flex-col items-center justify-center"
          }
        >
          <div className="text-emerald-200 text-[10px] uppercase tracking-wide">Bid</div>
          <div className="text-emerald-300 font-bold text-3xl min-[768px]:max-[1535px]:text-4xl leading-none">
            {bid}
          </div>
      </motion.div>
      {tagRow("font-semibold", <span className="text-[10px] text-emerald-200">{tag}</span>)}
    </div>
  );
}

export function TrumpSymbol({ suit }: { suit: Suit }) {
  return (
    <span className={SUIT_IS_RED[suit] ? "text-red-300" : "text-zinc-200"}>
      {SUIT_SYMBOL[suit]} {SUIT_LABEL[suit]}
    </span>
  );
}

/** Suit badge at table-playing-card centre pip scale (e.g. partner row). */
export function TrumpSuitHero({ suit }: { suit: Suit }) {
  const cls = SUIT_IS_RED[suit]
    ? "text-red-400 drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]"
    : // Clubs/spades read as black (not white-on-felt)
      "text-neutral-950 drop-shadow-[0_1px_3px_rgba(255,255,255,0.35)]";
  return (
    <span
      className={`block leading-none ${PLAYING_CARD_TABLE_CENTER_PIP} ${cls}`}
      role="img"
      aria-label={`Trump suit ${SUIT_LABEL[suit]}`}
    >
      {SUIT_SYMBOL[suit]}
    </span>
  );
}

// ScoreBadge — shows a team's score with a 0→11 progress bar underneath.
// At score >= 10 the bar picks up an amber glow ("match point"). Used in
// both the single-player and online-match top bars.
export function ScoreBadge({
  label, score, highlight,
}: {
  label: string;
  score: number;
  highlight?: boolean;
}) {
  const clamped = Math.max(0, Math.min(11, score));
  const pct = (clamped / 11) * 100;
  const nearWin = clamped >= 10;
  return (
    <div
      className={
        "rounded-xl px-3 py-1.5 text-sm min-w-[90px] " +
        (highlight
          ? "bg-emerald-500/20 border border-emerald-400/30"
          : "bg-white/5 border border-white/10")
      }
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xs text-white/60">{label}</div>
        <div className="font-bold text-lg leading-tight">{score}</div>
      </div>
      <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
        <motion.div
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ type: "spring", stiffness: 180, damping: 22 }}
          className={
            "h-full rounded-full " +
            (nearWin
              ? "bg-amber-300 shadow-[0_0_8px_rgba(252,211,77,0.7)]"
              : highlight
                ? "bg-emerald-400"
                : "bg-white/50")
          }
        />
      </div>
    </div>
  );
}

// Animated "your turn" chip — pulses softly so the player notices it's on
// them. Use above the hand fan when seat 0 / mySeat needs to act.
export function TurnChip({ label = "Your turn" }: { label?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold
                 bg-emerald-400/20 border border-emerald-300/40 text-emerald-200
                 shadow-[0_0_16px_rgba(52,211,153,0.35)]"
    >
      <motion.span
        animate={{ opacity: [1, 0.4, 1] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        className="w-1.5 h-1.5 rounded-full bg-emerald-300"
      />
      {label}
    </motion.div>
  );
}

// A fan-layout for the current player's cards. Rotation matches a full six;
// horizontal stride always uses the same step as between neighbors in that
// full hand ((container − cardWidth) / 5), then fewer cards are recentered —
// overlap stays visually consistent from 6 down to two.
//
// Props:
//   cards: sorted hand to display
//   legalKeys: a Set of "{suit}{rank}" strings representing currently-playable cards
//   onPlay: click handler; called only for legal cards
//   isPlayingPhase: true during the play phase (controls dim vs fan state)
//   disabled: global lock (e.g. network request in flight)
const CARD_W_DESKTOP = 64;
/** Horizontal gaps as if displaying six cards — keeps neighbor overlap identical for 2–6. */
const HAND_FAN_BASE_GAPS = 5;

/**
 * Hand card width in CSS px — must stay aligned with `PlayingCard` handheld tiers
 * (phone / tablet / desktop).
 */
export function touchHandCardWidthPx(viewportWidth: number): number {
  if (viewportWidth >= 1536) return 64;
  if (viewportWidth >= 768) return 108; // 6.75rem — iPad-class widths
  return 90; // 5.625rem — phones
}

function useTouchHandCardWidthPx(): number {
  const [px, setPx] = useState(90);
  useEffect(() => {
    const sync = () => setPx(touchHandCardWidthPx(window.innerWidth));
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);
  return px;
}

/** Matches `HandFan` min vertical space by tier. Used while fan unmounts during deal. */
export const HAND_FAN_DEAL_SPACER_CLASS =
  "min-h-[168px] min-[768px]:max-[1535px]:min-h-[196px] min-[1536px]:min-h-[128px]";

/** True when we should lay out the player's hand like a tablet/phone — not a wide desktop viewport. */
function useNarrowHandLayout(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    // Phones + iPads stay in touch hand sizing (paired with `touchHandCardWidthPx` + PlayingCard handheld).
    // 13" class iPads commonly report 1024×1366 (portrait) or 1366×1024 — both were crossing the old `lg`
    // breakpoint early and exploding stride with tiny 64px cards.
    const mq = window.matchMedia("(max-width: 1535px)");
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return narrow;
}

export function HandFan({
  cards, legalKeys, onPlay, isPlayingPhase, disabled,
}: {
  cards: Card[];
  legalKeys: Set<string>;
  onPlay: (c: Card) => void;
  isPlayingPhase: boolean;
  disabled?: boolean;
}) {
  const N = cards.length;
  const narrow = useNarrowHandLayout();
  const touchCardW = useTouchHandCardWidthPx();
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredW, setMeasuredW] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      setMeasuredW(w);
    });
    ro.observe(el);
    setMeasuredW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // Same θ as a full six-card fan (narrow = flat row for stability).
  const θ = narrow ? 0 : 4;
  const cardW = narrow ? touchCardW : CARD_W_DESKTOP;
  const fallbackW = narrow ? (touchCardW > 95 ? 920 : 390) : 720;
  const layoutW = measuredW > 0 ? measuredW : fallbackW;

  const strideBetweenCards = useMemo(() => {
    const minStride = narrow ? 8 : 12;
    const distribute = Math.max(minStride, (layoutW - cardW) / HAND_FAN_BASE_GAPS);
    // On wide canvases `(layout - card)/5` can become huge → a sparse broken-looking row. Cap overlap so six
    // cards stay clustered even when centered in a huge flex area (tablet landscape, ultrawide).
    const maxStride = narrow
      ? Math.max(minStride + 10, Math.round(cardW * 0.72))
      : Math.max(minStride + 6, Math.round(cardW * 1.28));
    return Math.min(distribute, maxStride);
  }, [narrow, layoutW, cardW]);

  const step = N <= 1 ? 0 : strideBetweenCards;

  const width = N > 0 ? (N - 1) * step + cardW : 0;
  // Each card's rotation pivot is offset below the card so the bottoms
  // stay roughly aligned — that's what makes it read as a "fan" and not
  // a "tilted row."
  const pivotY = narrow ? Math.round(72 + touchCardW * 0.42) : 80;
  // Vertical arc: flat on phone (stable row; avoids one card "jumping").
  const arcAmplitudePx = narrow ? 0 : 12;

  const fanHeight = narrow ? Math.round(120 + touchCardW * 0.53) : 128;

  return (
    <div
      ref={containerRef}
      className={
        // Full width for measurement; bleed past padding on narrower viewports where the tray is cramped.
        "w-full max-[1535px]:-mx-4 max-[1535px]:w-[calc(100%+2rem)]"
      }
    >
      <div
        className="relative flex w-full justify-center"
        style={{ minHeight: fanHeight }}
      >
        <div className="relative h-full shrink-0" style={{ width, minHeight: fanHeight }}>
          {cards.map((c, i) => {
            const key = `${c.suit}${c.rank}`;
            const isLegal = legalKeys.has(key);
            const canPlay = isPlayingPhase ? isLegal : false;
            const dimmed = isPlayingPhase && !isLegal;
            const angle = narrow ? 0 : (-θ * (N - 1) / 2 + θ * i);
            const t = N <= 1 ? 0 : (2 * i) / (N - 1) - 1;
            const arcFactor = 1 - t * t;
            const arcY = -arcAmplitudePx * arcFactor;

            return (
              <div
                key={key}
                className={
                  "absolute top-2 sm:transition-transform sm:duration-150 " +
                  "sm:hover:-translate-y-3 sm:hover:!rotate-0 sm:hover:z-50"
                }
                style={{
                  left: i * step,
                  transform: `rotate(${angle}deg) translateY(${arcY}px)`,
                  transformOrigin: `50% ${pivotY}px`,
                  zIndex: i,
                }}
              >
                <PlayingCard
                  card={c}
                  handheld
                  onClick={canPlay && !disabled ? () => onPlay(c) : undefined}
                  disabled={dimmed || disabled}
                  highlight={canPlay}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// A minimalist face-down card used for deal-animation flights. Felt-green
// gradient base, gold crosshatch, thin gold inset frame, centered "S"
// emblem. Same footprint as PlayingCard so we can fly one and land the
// other without resizing.
export function CardBack({ className = "" }: { className?: string }) {
  return (
    <div
      className={
        "w-16 h-24 min-[768px]:max-[1535px]:w-[6rem] min-[768px]:max-[1535px]:h-[9rem] " +
        "rounded-lg overflow-hidden relative " +
        "bg-gradient-to-br from-emerald-800 to-emerald-950 " +
        "shadow-[0_2px_6px_rgba(0,0,0,0.45)] border border-amber-300/40 " +
        className
      }
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 64 96"
        preserveAspectRatio="none"
      >
        {/* Diagonal crosshatch — draw lines explicitly to avoid shared
            <defs> ids across multiple card instances. */}
        {[-96, -72, -48, -24, 0, 24, 48, 72, 96].map(o => (
          <line
            key={`d${o}`}
            x1={o} y1={0} x2={o + 96} y2={96}
            stroke="#fbbf24" strokeWidth="0.5" opacity="0.22"
          />
        ))}
        {[-96, -72, -48, -24, 0, 24, 48, 72, 96].map(o => (
          <line
            key={`u${o}`}
            x1={o} y1={96} x2={o + 96} y2={0}
            stroke="#fbbf24" strokeWidth="0.5" opacity="0.22"
          />
        ))}
      </svg>
      {/* Inset gold frame */}
      <div className="absolute inset-1 rounded-md border border-amber-300/40 pointer-events-none" />
      {/* Center emblem */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-8 h-8 min-[768px]:max-[1535px]:w-10 min-[768px]:max-[1535px]:h-10 rounded-full border border-amber-300/70 flex items-center justify-center">
          <span className="font-serif font-bold text-amber-300 text-lg min-[768px]:max-[1535px]:text-xl leading-none">
            S
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- Deal animation -----------------------------------------------
// Renders an absolute-positioned overlay of face-down cards that fly
// from the dealer's on-screen seat to each player's seat, 3 cards at a
// time, clockwise from dealer+1. Two passes = 6 cards per player.
//
// The parent container must be `position: relative` and sized — the
// animation measures its bounding rect to compute pixel flight deltas.

// Screen-slot positions (percent of container). Aligns with the 3x3 grid
// used by TrickArea/CenterTable: seats sit in the middle cell of each
// outer edge.
//   0 = bottom (me / seat 0)
//   1 = left
//   2 = top
//   3 = right
const SLOT_POS: Record<SeatIndex, [number, number]> = {
  0: [50, 85],
  1: [15, 50],
  2: [50, 15],
  3: [85, 50],
};

/** Tuned for snappy online deal — overlay also ends as soon as private hand lands. */
const DEAL_BURST_INTERVAL_MS = 130;
const DEAL_FLIGHT_MS = 320;

function useDealAnimationCardHalf(): { w: number; h: number } {
  const [{ w, h }, setHalf] = useState({ w: 32, h: 48 });
  useEffect(() => {
    const sync = () => {
      const iw = window.innerWidth;
      if (iw >= 768 && iw < 1536) setHalf({ w: 48, h: 72 });
      else setHalf({ w: 32, h: 48 });
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);
  return { w, h };
}

export function DealAnimation({
  dealerSlot, onDone,
}: {
  // The on-screen slot of the dealer (0=bottom, 1=left, 2=top, 3=right).
  // In PlayPage this is just dealer-seat-index; in OnlinePlayPage it's
  // viewOf(hand.dealer) because the local player is always at the bottom.
  dealerSlot: SeatIndex;
  onDone: () => void;
}) {
  const { w: CARD_HALF_W, h: CARD_HALF_H } = useDealAnimationCardHalf();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  // Measure before paint — `useEffect` left one frame where the overlay had no strokes and
  // face-up cards showed through underneath the transparent `absolute inset-0` shell.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setDims({
        w: Math.max(1, rect.width),
        h: Math.max(1, rect.height),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Build the burst schedule: clockwise from dealer+1, two passes.
  const bursts = useMemo(() => {
    const out: { destSlot: SeatIndex; startMs: number }[] = [];
    let t = 0;
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 1; i <= 4; i++) {
        out.push({
          destSlot: (((dealerSlot + i) % 4) as SeatIndex),
          startMs: t,
        });
        t += DEAL_BURST_INTERVAL_MS;
      }
    }
    return out;
  }, [dealerSlot]);

  const totalMs =
    bursts[bursts.length - 1].startMs + DEAL_FLIGHT_MS + 120;

  useEffect(() => {
    const id = setTimeout(onDone, totalMs);
    return () => clearTimeout(id);
  }, [totalMs, onDone]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none z-30"
    >
      {dims && bursts.flatMap((b, bi) => {
        const [sx, sy] = SLOT_POS[dealerSlot];
        const [dx, dy] = SLOT_POS[b.destSlot];
        // Pixel delta from destination to source (cards start offset there
        // and animate x→0, y→0 to arrive at destination).
        const dxPx = ((sx - dx) / 100) * dims.w;
        const dyPx = ((sy - dy) / 100) * dims.h;
        return [0, 1, 2].map(i => {
          const delay = (b.startMs + i * 70) / 1000;
          // Slight jitter at destination so the 3 cards don't stack
          // identically.
          const jitterX = (i - 1) * 5;
          const jitterY = (i - 1) * 3;
          return (
            <motion.div
              key={`${bi}-${i}`}
              className="absolute"
              style={{
                left: `${dx}%`,
                top: `${dy}%`,
                // Center the card on its positioned point via negative
                // margin — motion's x/y are used for the flight offset.
                marginLeft: -CARD_HALF_W + jitterX,
                marginTop: -CARD_HALF_H + jitterY,
              }}
              initial={{ x: dxPx, y: dyPx, opacity: 0, scale: 0.55, rotate: 0 }}
              animate={{
                x: 0,
                y: 0,
                opacity: [0, 1, 1, 0],
                scale: [0.55, 1, 1, 0.95],
                rotate: 360,
              }}
              transition={{
                duration: DEAL_FLIGHT_MS / 1000,
                delay,
                ease: "easeOut",
                times: [0, 0.1, 0.88, 1],
              }}
            >
              <CardBack />
            </motion.div>
          );
        });
      })}
    </div>
  );
}

// Compact hand-result summary rendered in the center of the table when a
// hand finishes. The parent is responsible for the surrounding layout —
// this component just renders content and relies on its container (a
// trick-area or center-table box) to size and frame it.
export function HandResultCenter({
  handNumber, bid, bidderLabel, score, labelTeam, abandoned,
}: {
  handNumber: number;
  bid?: BidValue;
  bidderLabel?: string;
  score?: HandScore;
  // Maps a TeamId to the page's local display string, e.g. "You" /
  // "Opp." for quick play, or "Team A" / "Team B" for online.
  labelTeam: (t?: TeamId) => string;
  abandoned?: boolean;
}) {
  if (abandoned) {
    return (
      <div className="text-center w-full max-h-full overflow-y-auto px-1 py-0.5">
        <div className="text-[11px] text-white/40 uppercase tracking-wide">Hand {handNumber}</div>
        <div className="text-amber-200 font-semibold text-sm mt-1">Abandoned</div>
        <div className="text-[11px] text-white/50 mt-0.5">
          All passed or coked — no points this hand.
        </div>
      </div>
    );
  }
  if (!score) {
    return (
      <div className="text-white/30 text-xs uppercase text-center">Hand complete</div>
    );
  }
  const aColor = score.teamA >= 0 ? "text-emerald-300" : "text-red-300";
  const bColor = score.teamB >= 0 ? "text-emerald-300" : "text-red-300";
  const aSign = score.teamA > 0 ? "+" : "";
  const bSign = score.teamB > 0 ? "+" : "";
  return (
    <div className="text-center w-full max-h-full overflow-y-auto px-2 py-1">
      <div className="flex items-baseline justify-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wide text-white/40">
          Hand {handNumber}
        </div>
        {bid !== undefined && bidderLabel && (
          <div className="text-[10px] text-white/50">
            · {bidderLabel} bid {bid}
          </div>
        )}
      </div>
      <div className="flex items-stretch justify-center gap-4 mt-2">
        <div className="text-center min-w-[4.5rem]">
          <div className="text-[10px] text-white/40 uppercase">{labelTeam("A")}</div>
          <div className={"text-2xl font-bold leading-none " + aColor}>
            {aSign}{score.teamA}
          </div>
        </div>
        <div className="w-px bg-white/10 my-0.5 shrink-0" />
        <div className="text-center min-w-[4.5rem]">
          <div className="text-[10px] text-white/40 uppercase">{labelTeam("B")}</div>
          <div className={"text-2xl font-bold leading-none " + bColor}>
            {bSign}{score.teamB}
          </div>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-[minmax(0,auto)_1fr] gap-x-3 gap-y-2 text-[11px] sm:text-xs max-w-[17rem] mx-auto text-left border border-white/10 rounded-lg px-3 py-2.5 bg-black/20">
        <dt className="text-white/45 font-medium uppercase tracking-wide">High</dt>
        <dd className="text-white/95 font-semibold text-right tabular-nums">{labelTeam(score.high)}</dd>
        <dt className="text-white/45 font-medium uppercase tracking-wide">Low</dt>
        <dd className="text-white/95 font-semibold text-right tabular-nums">{labelTeam(score.low)}</dd>
        <dt className="text-white/45 font-medium uppercase tracking-wide">Game</dt>
        <dd className="text-white/95 font-semibold text-right tabular-nums">
          {labelTeam(score.game)} · {score.gamePointsA}–{score.gamePointsB}
        </dd>
        <dt className="text-white/45 font-medium uppercase tracking-wide">Jack</dt>
        <dd className="text-white/95 font-semibold text-right tabular-nums">{labelTeam(score.jack)}</dd>
      </dl>
      {!score.bidderMade && (
        <div className="text-red-300 text-[11px] font-semibold mt-2">Set back</div>
      )}
    </div>
  );
}
